import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

const EFFECTIVE_DATE = "March 1, 2026";

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    content: `By accessing or using the Opero platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms. If you do not agree to these Terms, you may not use the Service.`,
  },
  {
    title: "2. Description of Service",
    content: `Opero is a task management platform that provides project tracking, team collaboration, and related productivity tools, including the Atlas AI assistant. The Service is provided on a subscription basis and may include free and paid tiers with varying feature sets.`,
  },
  {
    title: "3. Account Registration",
    content: `To use the Service, you must create an account with accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account. We reserve the right to suspend or terminate accounts that violate these Terms.`,
  },
  {
    title: "4. Acceptable Use",
    content: `You agree not to use the Service to:\n\n- Violate any applicable laws or regulations\n- Infringe on the intellectual property rights of others\n- Transmit malicious code, viruses, or harmful content\n- Attempt to gain unauthorized access to our systems or other users' accounts\n- Use the Service for any unlawful, harassing, or abusive purpose\n- Interfere with the operation or performance of the Service\n- Reverse engineer, decompile, or disassemble any part of the Service`,
  },
  {
    title: "5. Intellectual Property",
    content: `The Service, including all software, designs, text, graphics, and other content, is owned by Digiits and protected by intellectual property laws. You retain ownership of any data or content you upload to the Service. By using the Service, you grant us a limited license to process your content solely for the purpose of providing the Service to you.`,
  },
  {
    title: "6. Payment and Billing",
    content: `Paid subscriptions are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law or as explicitly stated in our refund policy. We reserve the right to change our pricing with 30 days' notice. Failure to pay may result in suspension or termination of your account.`,
  },
  {
    title: "7. Data Privacy",
    content: `Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to the practices described in the Privacy Policy.`,
  },
  {
    title: "8. Termination",
    content: `Either party may terminate the agreement at any time. You may cancel your subscription through your account settings. We may suspend or terminate your access if you violate these Terms, fail to pay applicable fees, or if we discontinue the Service. Upon termination, your right to use the Service ceases immediately. You may request export of your data within 30 days of termination.`,
  },
  {
    title: "9. Limitation of Liability",
    content: `To the maximum extent permitted by law, Opero and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, or goodwill, arising out of or related to your use of the Service. Our total liability for any claims arising under these Terms shall not exceed the amount you paid us in the 12 months preceding the claim.`,
  },
  {
    title: "10. Disclaimer of Warranties",
    content: `The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or secure.`,
  },
  {
    title: "11. Governing Law",
    content: `These Terms shall be governed by and construed in accordance with the laws of the Republic of Ghana, without regard to conflict of law principles. Any disputes arising under these Terms shall be resolved in the courts of Ghana.`,
  },
  {
    title: "12. Changes to Terms",
    content: `We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms on our website and updating the effective date. Your continued use of the Service after the changes take effect constitutes your acceptance of the revised Terms.`,
  },
  {
    title: "13. Contact",
    content: `If you have questions about these Terms, please contact us at corporate@digiits.com or write to: Digiits, Accra, Ghana.`,
  },
];

export function TermsPage() {
  return (
    <main className="pt-28 pb-20">
      <article className="mx-auto max-w-3xl px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          Terms of Service
        </h1>
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          Effective date: {EFFECTIVE_DATE}
        </p>

        <div className="mt-6 border-b border-slate-200 dark:border-slate-800 pb-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          Please read these Terms of Service carefully before using the Opero
          platform operated by Digiits.
        </div>

        <div className="mt-8 space-y-10">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {section.title}
              </h2>
              <div className="mt-3 space-y-3">
                {section.content.split("\n\n").map((block, i) => {
                  const trimmed = block.trim();
                  if (trimmed.startsWith("- ")) {
                    const items = trimmed.split("\n").filter((l) => l.trim());
                    return (
                      <ul
                        key={i}
                        className="list-disc space-y-1.5 pl-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                      >
                        {items.map((item, j) => (
                          <li key={j}>{item.replace(/^-\s*/, "")}</li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p
                      key={i}
                      className="text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                    >
                      {trimmed}
                    </p>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 border-t border-slate-200 dark:border-slate-800 pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </article>
    </main>
  );
}
